import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  CircularProgress,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemAvatar,
  Avatar,
  CardMedia,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  ShoppingCart as ShoppingCartIcon,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getPromotions,
  createPromotion,
  updatePromotion,
  deletePromotion,
  clearError,
  clearSuccess,
} from '../store/slices/promotionSlice';
import { getProducts } from '../store/slices/productSlice';

const Promotions = () => {
  const dispatch = useDispatch();
  const { promotions, isLoading, error, success } = useSelector((state) => state.promotions);
  const { products } = useSelector((state) => state.products);

  const [openDialog, setOpenDialog] = useState(false);
  const [openProductModal, setOpenProductModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    description: '',
    type: 'banner',
    discountPercentage: 0,
    startDate: '',
    endDate: '',
    isActive: true,
    image: '',
    products: [],
    backgroundColor: 'linear-gradient(135deg, #ff9ec4 0%, #ff6b9d 50%, #c44569 100%)',
    textColor: '#ffffff',
    productCardColor: '#ff6b9d',
    priority: 1,
    displaySettings: {
      changeInterval: 2000,
      fadeDuration: 300
    }
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    dispatch(getPromotions());
    dispatch(getProducts({ limit: 1000 }));
  }, [dispatch]);

  // Handle success state
  useEffect(() => {
    if (success) {
      handleCloseDialog();
      dispatch(clearSuccess());
      // Refresh the promotions list to show the new promotion
      dispatch(getPromotions());
    }
  }, [success, dispatch]);

  // Handle error state
  useEffect(() => {
    if (error) {
      console.error('Promotion error:', error);
    }
  }, [error]);

  const handleOpenDialog = (promotion = null) => {
    if (promotion) {
      setEditingPromotion(promotion);
      setFormData({
        title: promotion.title || '',
        subtitle: promotion.subtitle || '',
        description: promotion.description || '',
        type: promotion.type || 'banner',
        discountPercentage: promotion.discountPercentage || 0,
        startDate: promotion.validFrom ? promotion.validFrom.split('T')[0] : '',
        endDate: promotion.validUntil ? promotion.validUntil.split('T')[0] : '',
        isActive: promotion.isActive !== undefined ? promotion.isActive : true,
        image: promotion.image || '',
        products: promotion.products ? (Array.isArray(promotion.products) ? promotion.products.map(p => typeof p === 'string' ? p : p._id || p) : []) : [],
        backgroundColor: promotion.backgroundColor || 'linear-gradient(135deg, #ff9ec4 0%, #ff6b9d 50%, #c44569 100%)',
        textColor: promotion.textColor || '#ffffff',
        productCardColor: promotion.productCardColor || '#ff6b9d',
        priority: promotion.priority || 1,
        displaySettings: {
          changeInterval: promotion.displaySettings?.changeInterval || 2000,
          fadeDuration: promotion.displaySettings?.fadeDuration || 300
        }
      });
      setImageFile(null);
      setImagePreview(promotion.image || null);
    } else {
      setEditingPromotion(null);
      setFormData({
        title: '',
        subtitle: '',
        description: '',
        type: 'banner',
        discountPercentage: 0,
        startDate: '',
        endDate: '',
        isActive: true,
        image: '',
        products: [],
        backgroundColor: 'linear-gradient(135deg, #ff9ec4 0%, #ff6b9d 50%, #c44569 100%)',
        textColor: '#ffffff',
        productCardColor: '#ff6b9d',
        priority: 1,
        displaySettings: {
          changeInterval: 2000,
          fadeDuration: 300
        }
      });
      setImageFile(null);
      setImagePreview(null);
    }
    setOpenDialog(true);
    dispatch(clearError());
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingPromotion(null);
    setFormData({
      title: '',
      subtitle: '',
      description: '',
      type: 'banner',
      discountPercentage: 0,
      startDate: '',
      endDate: '',
      isActive: true,
      image: '',
      products: [],
      backgroundColor: 'linear-gradient(135deg, #ff9ec4 0%, #ff6b9d 50%, #c44569 100%)',
      textColor: '#ffffff',
      productCardColor: '#ff6b9d',
      priority: 1,
      displaySettings: {
        changeInterval: 2000,
        fadeDuration: 300
      }
    });
    setImageFile(null);
    setImagePreview(null);
  };

  const handleToggleProduct = (productId) => {
    setFormData(prev => ({
      ...prev,
      products: prev.products.includes(productId)
        ? prev.products.filter(id => id !== productId)
        : [...prev.products, productId]
    }));
  };

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase())
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    dispatch(clearError());

    console.log('Submitting promotion:', { editingPromotion, formData, imageFile });

    // Validate required fields
    if (!formData.title || !formData.startDate || !formData.endDate) {
      dispatch(clearError());
      // You might want to show a more user-friendly error here
      return;
    }

    const submitData = {
      title: formData.title,
      subtitle: formData.subtitle || '',
      description: formData.description || '',
      type: formData.type,
      image: formData.image || '',
      products: formData.products || [],
      backgroundColor: formData.backgroundColor,
      textColor: formData.textColor || '#ffffff',
      productCardColor: formData.productCardColor || '#ff6b9d',
      validFrom: new Date(formData.startDate + 'T00:00:00').toISOString(),
      validUntil: new Date(formData.endDate + 'T23:59:59').toISOString(),
      discountPercentage: Number(formData.discountPercentage) || 0,
      priority: Number(formData.priority) || 1,
      isActive: formData.isActive !== undefined ? formData.isActive : true,
      displaySettings: formData.displaySettings || { changeInterval: 2000, fadeDuration: 300 },
    };

    // If there's a file upload, we need to handle it differently
    if (imageFile) {
      console.log('Frontend: Handling image file upload');
      const formDataWithFile = new FormData();
      formDataWithFile.append('image', imageFile);
      Object.keys(submitData).forEach(key => {
        if (key === 'displaySettings' || key === 'products') {
          formDataWithFile.append(key, JSON.stringify(submitData[key]));
        } else {
          formDataWithFile.append(key, submitData[key]);
        }
      });

      if (editingPromotion && editingPromotion._id) {
        console.log('Frontend: Updating promotion with new image');
        dispatch(updatePromotion({ id: editingPromotion._id, promotionData: formDataWithFile }));
      } else {
        console.log('Frontend: Creating promotion with image');
        dispatch(createPromotion(formDataWithFile));
      }
    } else {
      console.log('Frontend: No image file, using existing image or URL');
      // For updates without new image, ensure we keep the existing image
      if (editingPromotion && editingPromotion._id) {
        const updateData = {
          ...submitData,
          image: formData.image || editingPromotion.image // Keep existing image if no new one provided
        };
        console.log('Frontend: Updating promotion without new image:', updateData);
        dispatch(updatePromotion({ id: editingPromotion._id, promotionData: updateData }));
      } else {
        console.log('Frontend: Creating promotion without image');
        dispatch(createPromotion(submitData));
      }
    }

    // Success handling is now done via useEffect
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this promotion?')) {
      dispatch(deletePromotion(id));
    }
  };

  const handleImageChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setFormData({ ...formData, image: '' });
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'hero': return 'warning';
      case 'banner': return 'secondary';
      case 'featured': return 'success';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            color: '#000000',
            fontWeight: 700,
            pb: 2,
            borderBottom: '2px solid #ff2852',
          }}
        >
          Promotions
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: '#ff2852',
            color: 'white',
            fontWeight: 600,
            '&:hover': {
              backgroundColor: '#5A189A',
            },
          }}
        >
          Add Promotion
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {promotions.length === 0 ? (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: '#f5f5f5',
            borderRadius: 2,
            border: '2px dashed #e0e0e0',
          }}
        >
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Promotions Found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create your first promotion to get started
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#ff2852',
              color: '#000000',
              fontWeight: 600,
              '&:hover': {
                backgroundColor: '#5A189A',
              },
            }}
          >
            Create Your First Promotion
          </Button>
        </Box>
      ) : (
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          justifyContent: 'flex-start',
          '@media (max-width: 600px)': {
            justifyContent: 'center',
            gap: 2,
          }
        }}>
          {promotions.map((promotion) => (
            <Card
              key={promotion._id}
              sx={{
                width: { xs: '100%', sm: '350px' },
                maxWidth: '350px',
                height: 'auto',
                display: 'flex',
                flexDirection: 'column',
                border: promotion.isActive ? '2px solid #4caf50' : '1px solid #e0e0e0',
                backgroundColor: promotion.isActive ? '#f8fff8' : '#ffffff',
                borderRadius: 2,
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(123, 44, 191, 0.25)',
                  borderColor: '#ff2852',
                },
              }}
            >
              {promotion.image && (
                <Box
                  component="img"
                  src={promotion.image}
                  alt={promotion.title}
                  sx={{
                    width: '100%',
                    height: '200px',
                    objectFit: 'cover',
                    borderTopLeftRadius: '8px',
                    borderTopRightRadius: '8px',
                  }}
                />
              )}
              <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      color: '#000000',
                      fontWeight: 600,
                      fontSize: '1.1rem',
                      lineHeight: 1.3,
                    }}
                  >
                    {promotion.title}
                  </Typography>
                  <Box>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(promotion)}
                      sx={{ color: '#666' }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(promotion._id)}
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>

                <Box mb={1.5} display="flex" gap={1} flexWrap="wrap">
                  <Chip
                    label={promotion.type.toUpperCase()}
                    color={getTypeColor(promotion.type)}
                    size="small"
                    sx={{ fontSize: '0.7rem', height: '24px' }}
                  />
                  <Chip
                    label={promotion.isActive ? 'Active' : 'Inactive'}
                    color={promotion.isActive ? 'success' : 'default'}
                    size="small"
                    icon={promotion.isActive ? <VisibilityIcon sx={{ fontSize: '14px !important' }} /> : <VisibilityOffIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{ fontSize: '0.7rem', height: '24px' }}
                  />
                </Box>

                {promotion.description && (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    mb={1.5}
                    sx={{
                      fontSize: '0.85rem',
                      lineHeight: 1.5,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {promotion.description}
                  </Typography>
                )}

                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: '#ff2852',
                      fontWeight: 600,
                      fontSize: '0.9rem',
                    }}
                  >
                    {promotion.discountPercentage}% OFF
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontSize: '0.8rem' }}
                  >
                    Priority: {promotion.priority}
                  </Typography>
                </Box>

                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                  sx={{
                    fontSize: '0.75rem',
                    mt: 1,
                    pt: 1,
                    borderTop: '1px solid #e0e0e0',
                  }}
                >
                  {formatDate(promotion.validFrom)} - {formatDate(promotion.validUntil)}
                </Typography>
              </CardContent>
            </Card>
          ))}
        </Box>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPromotion ? 'Edit Promotion' : 'Add New Promotion'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}
            {success && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Promotion {editingPromotion ? 'updated' : 'created'} successfully!
              </Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Subtitle"
                  value={formData.subtitle}
                  onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                  placeholder="e.g., The Chic Edit"
                  helperText="Secondary headline text (optional)"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  multiline
                  rows={3}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    label="Type"
                  >
                    <MenuItem value="banner">Banner</MenuItem>
                    <MenuItem value="hero">Hero</MenuItem>
                    <MenuItem value="featured">Featured</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Discount Percentage"
                  type="number"
                  value={formData.discountPercentage}
                  onChange={(e) => setFormData({ ...formData, discountPercentage: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Start Date"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="End Date"
                  type="date"
                  value={formData.endDate}
                  onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Promotion Image
                </Typography>

                {/* Image Preview */}
                {imagePreview && (
                  <Box sx={{ mb: 2 }}>
                    <img
                      src={imagePreview}
                      alt="Preview"
                      style={{
                        width: '100%',
                        maxWidth: '300px',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #ddd'
                      }}
                    />
                    <Button
                      size="small"
                      color="error"
                      onClick={handleRemoveImage}
                      sx={{ mt: 1 }}
                    >
                      Remove Image
                    </Button>
                  </Box>
                )}

                {/* File Upload */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <input
                    accept="image/*"
                    style={{ display: 'none' }}
                    id="image-upload"
                    type="file"
                    onChange={handleImageChange}
                  />
                  <label htmlFor="image-upload">
                    <Button
                      variant="outlined"
                      component="span"
                      startIcon={<ImageIcon />}
                    >
                      Choose from Gallery
                    </Button>
                  </label>

                  <Typography variant="body2" color="text.secondary">
                    or
                  </Typography>

                  <TextField
                    fullWidth
                    label="Image URL"
                    value={formData.image}
                    onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                    placeholder="https://example.com/image.jpg"
                    size="small"
                    sx={{ maxWidth: 300 }}
                  />
                </Box>

                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Upload an image from your computer or paste an image URL
                </Typography>
              </Grid>
              {/* Product Selection */}
              <Grid item xs={12}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                    Products
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<ShoppingCartIcon />}
                    onClick={() => setOpenProductModal(true)}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    {formData.products.length > 0
                      ? `${formData.products.length} Product${formData.products.length > 1 ? 's' : ''} Selected`
                      : 'Select Products'
                    }
                  </Button>
                  {formData.products.length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                      {formData.products.map(productId => {
                        const product = products.find(p => p._id === productId);
                        return product ? (
                          <Chip
                            key={productId}
                            label={product.name}
                            onDelete={() => handleToggleProduct(productId)}
                            size="small"
                          />
                        ) : null;
                      })}
                    </Box>
                  )}
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Select products to display in the hero banner carousel
                  </Typography>
                </Box>
              </Grid>

              {/* Color Settings */}
              {formData.type === 'hero' && (
                <>
                  <Grid item xs={12}>
                    <Typography variant="h6" sx={{ mb: 2, mt: 2, color: '#ff2852', fontWeight: 600 }}>
                      Color Settings
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Background Color/Gradient"
                      value={formData.backgroundColor}
                      onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                      placeholder="linear-gradient(135deg, #ff9ec4 0%, #ff6b9d 50%, #c44569 100%)"
                      helperText="CSS gradient or color"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <input
                              type="color"
                              value={formData.backgroundColor.startsWith('#') ? formData.backgroundColor : '#ff6b9d'}
                              onChange={(e) => setFormData({ ...formData, backgroundColor: e.target.value })}
                              style={{ width: 40, height: 40, border: 'none', cursor: 'pointer', borderRadius: 4 }}
                            />
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Text Color"
                      type="color"
                      value={formData.textColor}
                      onChange={(e) => setFormData({ ...formData, textColor: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {formData.textColor}
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Product Card Color"
                      type="color"
                      value={formData.productCardColor}
                      onChange={(e) => setFormData({ ...formData, productCardColor: e.target.value })}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Typography variant="body2" sx={{ mr: 1 }}>
                              {formData.productCardColor}
                            </Typography>
                          </InputAdornment>
                        )
                      }}
                    />
                  </Grid>
                </>
              )}

              {/* Display Settings Section */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, mt: 2, color: '#ff2852', fontWeight: 600 }}>
                  Display Settings
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Change Interval (milliseconds)"
                  type="number"
                  value={formData.displaySettings?.changeInterval || 2000}
                  onChange={(e) => setFormData({
                    ...formData,
                    displaySettings: {
                      ...(formData.displaySettings || {}),
                      changeInterval: parseInt(e.target.value) || 2000
                    }
                  })}
                  helperText="How often the promotion changes (1000 = 1 second)"
                  inputProps={{ min: 1000, max: 30000, step: 500 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Fade Duration (milliseconds)"
                  type="number"
                  value={formData.displaySettings?.fadeDuration || 300}
                  onChange={(e) => setFormData({
                    ...formData,
                    displaySettings: {
                      ...(formData.displaySettings || {}),
                      fadeDuration: parseInt(e.target.value) || 300
                    }
                  })}
                  helperText="How fast the transition animation is (100 = very fast, 1000 = slow)"
                  inputProps={{ min: 100, max: 2000, step: 50 }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Priority"
                  type="number"
                  value={formData.priority}
                  onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
                  helperText="Higher number = higher priority"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formData.isActive}
                      onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                    />
                  }
                  label="Active"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">
              {editingPromotion ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Product Selection Modal */}
      <Dialog
        open={openProductModal}
        onClose={() => setOpenProductModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Select Products</Typography>
            <IconButton onClick={() => setOpenProductModal(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Search Products"
            value={productSearch}
            onChange={(e) => setProductSearch(e.target.value)}
            sx={{ mb: 2 }}
            placeholder="Type to search products..."
          />
          <Box sx={{ maxHeight: 500, overflowY: 'auto' }}>
            {products.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                No products available
              </Typography>
            ) : (
              <List>
                {filteredProducts.map((product) => (
                  <ListItem
                    key={product._id}
                    disablePadding
                    secondaryAction={
                      <Checkbox
                        edge="end"
                        checked={formData.products.includes(product._id)}
                        onChange={() => handleToggleProduct(product._id)}
                      />
                    }
                  >
                    <ListItemButton onClick={() => handleToggleProduct(product._id)}>
                      <ListItemAvatar>
                        <Avatar
                          variant="rounded"
                          src={product.images && product.images[0] ? product.images[0] : ''}
                          sx={{ width: 56, height: 56 }}
                        >
                          {product.name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={product.name}
                        secondary={`PKR ${product.price?.toLocaleString() || '0'}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenProductModal(false)}>Done</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Promotions;